import React from 'react';
import { useTranslation } from 'react-i18next';
import CustomTable from '../../../../../../../components/table/CustomTable';
import CustomTh from '../../../../../../../components/table/CustomTh';
import CustomTd from '../../../../../../../components/table/CustomTd';
import {currencyFormat, formatTimeUnit, getDateFormat} from '../../../../../../../Apis/functions';
import {
    getCalculationTypeById,
    POLICY_CONDITION_MODEL
} from '../../../OvertimePolicies/Global/CreateUpdatePolicy/Model/Model';
import TableFunction from '../../../../../../../components/table/TableFunction';
import BreakdownHeaderCell from './BreakdownHeaderCell';
import ScheduledShiftCell from './ScheduledShiftCell';

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * Compound breakdown table: shows the full rate progression where each entry
 * stacks on the previous accumulated rate (previous → effective → delta).
 * @param {Object} item - Overtime line snapshot.
 */
const CompoundBreakdownTable = ({ item }) => {
    const { t } = useTranslation();
    const tableId = 'tbl_obd_compound_breakdown';
    const type = getCalculationTypeById(item?.policyCalculationTypeSnapshot);
    const calculationValue = item?.policyCalculationValueSnapshot;
    const rows = item?.items ?? [];
    const {
        effectiveRateTitle,
        rateDeltaTitle,
    } = POLICY_CONDITION_MODEL[item?.policyConditionSnapshot]?.getFormulaCompound({
        type,
        calculationValue
    });

    const getThead = () => [
        <BreakdownHeaderCell key='scheduled_shift' tableId={tableId} columnKey='scheduled_shift' customWidth={220}/>,
        <BreakdownHeaderCell key='entry_date' tableId={tableId} columnKey='entry_date' customWidth={150}/>,
        <BreakdownHeaderCell key='exit_date' tableId={tableId} columnKey='exit_date' customWidth={150}/>,
        <BreakdownHeaderCell key='previous_rate' tableId={tableId} columnKey='previous_rate' />,
        <BreakdownHeaderCell key='effective_rate' tableId={tableId} columnKey='effective_rate'
            value={effectiveRateTitle} />,
        <BreakdownHeaderCell key='rate_delta' tableId={tableId} columnKey='rate_delta'
            value={rateDeltaTitle} />,
        <BreakdownHeaderCell key='covered_hours' tableId={tableId} columnKey='covered_hours' />,
        <BreakdownHeaderCell key='operation' tableId={tableId} columnKey='operation' customWidth={150}
            value={`${t('rate_delta')} x ${t('covered_hours')}`} />,
        <CustomTh key='equals' tableId={tableId} classes={'breakdown-fixed-equals'} />,
        <CustomTh key='currency' tableId={tableId} classes={'breakdown-fixed-currency'} />,
        <BreakdownHeaderCell key='result' tableId={tableId} columnKey='result' classes={'breakdown-fixed-col'} />,
    ];

    const getTbody = () => ({
        data: rows.map((_, i) => ({ id: i })),
        body: [
            ...rows.map((e) => {
                const rateBefore = `$${currencyFormat(e.rateBefore)}`;
                const rateDelta = `$${currencyFormat(e.rateDelta)}`;
                const effectiveRate = `$${currencyFormat(e.effectiveRate)}`;
                const result = `${currencyFormat(e.result)}`;
                const totalOvertimeMinutes = `${formatTimeUnit(e.totalOvertimeMinutes)}`;
                const operationFormula = POLICY_CONDITION_MODEL[item?.policyConditionSnapshot]?.getFormulaCompound({
                    type,
                    calculationValue,
                    totalOvertimeMinutes,
                    rateDelta
                })?.operationFormula;

                return [
                    <CustomTd key='scheduled_shift' tableId={tableId}>
                        <ScheduledShiftCell startDate={e?.scheduledStartDate} endDate={e?.scheduledEndDate} />
                    </CustomTd>,
                    <CustomTd key='entry_date' tableId={tableId}>{getDateFormat(e?.entryDate, true)}</CustomTd>,
                    <CustomTd key='exit_date' tableId={tableId}>{getDateFormat(e?.exitDate, true)}</CustomTd>,
                    <CustomTd key='previous_rate' tableId={tableId} classes='text-end'>{rateBefore}</CustomTd>,
                    <CustomTd key='effective_rate' tableId={tableId} classes='text-end'>{effectiveRate}</CustomTd>,
                    <CustomTd key='rate_delta' tableId={tableId} classes='text-end'>{rateDelta}</CustomTd>,
                    <CustomTd key='covered_hours' tableId={tableId} classes='text-end'>{totalOvertimeMinutes}</CustomTd>,
                    <CustomTd key='operation' tableId={tableId} classes='text-end'>
                        {operationFormula}
                    </CustomTd>,
                    <CustomTd key='equals' tableId={tableId} classes='text-center breakdown-fixed-equals'>=</CustomTd>,
                    <CustomTd key='currency' tableId={tableId} classes='text-center breakdown-fixed-currency'>$</CustomTd>,
                    <CustomTd key='result' tableId={tableId} classes='text-end breakdown-fixed-col'>{result}</CustomTd>,
                ];
            }),
        ],
    });

    const { thead, tbody } = TableFunction({ getThead, getTbody, data: { content: rows }, dependencies: [rows] });

    return (
        <div className='overflow-x-auto custom-scrollbar'>
            <CustomTable
                tableId={tableId}
                thead={thead}
                tbody={tbody}
                columnActions={false}
                inModal={true}
                withoutHeight={true}
            />
        </div>
    );
};

export default CompoundBreakdownTable;
